import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import {
  API,
  adminApi,
  getAdminToken,
  setAdminToken,
  formatApiErrorDetail,
} from "../lib/adminApi";
import {
  LogOut,
  Star,
  Check,
  Trash2,
  Eye,
  EyeOff,
  RefreshCw,
  ShieldCheck,
  ArrowLeft,
} from "lucide-react";
import { SITE } from "../lib/site";

export default function AdminPage() {
  const [authChecked, setAuthChecked] = useState(false);
  const [user, setUser] = useState(null);

  // On mount: try /auth/me to validate existing token
  useEffect(() => {
    (async () => {
      const t = getAdminToken();
      if (!t) {
        setAuthChecked(true);
        return;
      }
      try {
        const { data } = await adminApi.get("/auth/me");
        setUser(data);
      } catch {
        setAdminToken(null);
      } finally {
        setAuthChecked(true);
      }
    })();
  }, []);

  if (!authChecked) {
    return (
      <div className="min-h-screen grid place-items-center bg-black text-white/60">
        Loading...
      </div>
    );
  }

  if (!user) return <LoginScreen onSuccess={setUser} />;
  return <Dashboard user={user} onLogout={() => { setAdminToken(null); setUser(null); }} />;
}

function LoginScreen({ onSuccess }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [show, setShow] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/auth/login`, { email, password });
      setAdminToken(data.token);
      onSuccess(data.user);
      toast.success("Welcome back!");
    } catch (e2) {
      const msg = formatApiErrorDetail(e2?.response?.data?.detail) || e2.message;
      setErr(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-black text-white flex flex-col">
      <header className="px-6 lg:px-10 h-20 flex items-center">
        <a
          href="/"
          data-testid="admin-back-home"
          className="inline-flex items-center gap-2 text-white/60 hover:text-gold text-sm uppercase tracking-[0.18em]"
        >
          <ArrowLeft size={16} /> Back to site
        </a>
      </header>

      <main className="flex-1 grid place-items-center px-6">
        <form
          onSubmit={submit}
          data-testid="admin-login-form"
          className="w-full max-w-md bg-[#0a0a0a] border border-white/10 p-10 rounded-sm relative"
        >
          <div className="absolute -top-px left-0 right-0 h-px bg-gradient-to-r from-transparent via-gold/60 to-transparent" />
          <div className="flex items-center gap-3 mb-8">
            <img
              src={SITE.media.logo}
              alt="Shine and Drive"
              className="h-12 w-12 object-contain"
            />
            <div>
              <div className="text-xs uppercase tracking-[0.28em] text-gold/80">
                Shine &amp; Drive
              </div>
              <div className="font-display text-2xl">Admin Sign-in</div>
            </div>
          </div>

          <div className="space-y-5">
            <div>
              <Label htmlFor="email" className="text-xs uppercase tracking-[0.22em] text-white/60">
                Email
              </Label>
              <Input
                id="email"
                type="email"
                autoFocus
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                data-testid="admin-login-email"
                className="gold-input mt-3 bg-black/40 border-white/15 text-white h-12"
              />
            </div>
            <div>
              <Label htmlFor="password" className="text-xs uppercase tracking-[0.22em] text-white/60">
                Password
              </Label>
              <div className="relative mt-3">
                <Input
                  id="password"
                  type={show ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  data-testid="admin-login-password"
                  className="gold-input bg-black/40 border-white/15 text-white h-12 pr-12"
                />
                <button
                  type="button"
                  onClick={() => setShow((v) => !v)}
                  data-testid="admin-login-toggle-show"
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-white/40 hover:text-gold"
                  aria-label={show ? "Hide password" : "Show password"}
                >
                  {show ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>
          </div>

          {err && (
            <div
              data-testid="admin-login-error"
              className="mt-5 text-sm text-red-400 bg-red-500/10 border border-red-500/30 px-4 py-3 rounded-sm"
            >
              {err}
            </div>
          )}

          <Button
            type="submit"
            disabled={submitting}
            data-testid="admin-login-submit"
            className="mt-8 w-full bg-gold text-black hover:bg-[#F3E5AB] rounded-full py-6 uppercase tracking-[0.22em] text-xs font-medium"
          >
            {submitting ? "Signing in..." : "Sign in"}
          </Button>
        </form>
      </main>
    </div>
  );
}

function Dashboard({ user, onLogout }) {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all"); // all | pending | approved

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await adminApi.get("/admin/reviews");
      setReviews(data);
    } catch (e) {
      toast.error("Could not load reviews. Try logging out and back in.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const approve = async (id, approved) => {
    try {
      const { data } = await adminApi.patch(`/admin/reviews/${id}`, { approved });
      setReviews((prev) => prev.map((r) => (r.id === id ? data : r)));
      toast.success(approved ? "Review published" : "Review unpublished");
    } catch {
      toast.error("Could not update review.");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this review? This cannot be undone.")) return;
    try {
      await adminApi.delete(`/admin/reviews/${id}`);
      setReviews((prev) => prev.filter((r) => r.id !== id));
      toast.success("Review deleted");
    } catch {
      toast.error("Could not delete review.");
    }
  };

  const filtered = reviews.filter((r) =>
    filter === "all" ? true : filter === "approved" ? r.approved : !r.approved
  );

  const counts = {
    all: reviews.length,
    pending: reviews.filter((r) => !r.approved).length,
    approved: reviews.filter((r) => r.approved).length,
  };

  return (
    <div className="min-h-screen bg-black text-white">
      <header className="border-b border-white/10 bg-black/80 backdrop-blur-md">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 h-20 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <a href="/" data-testid="admin-back-home" className="hidden sm:inline-flex items-center gap-2 text-white/60 hover:text-gold text-xs uppercase tracking-[0.18em]">
              <ArrowLeft size={14} /> Site
            </a>
            <div className="h-8 w-px bg-white/10 hidden sm:block" />
            <div className="flex items-center gap-2">
              <ShieldCheck size={16} className="text-gold" />
              <span className="font-display text-xl">Reviews Admin</span>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <span className="hidden sm:inline text-xs text-white/45 uppercase tracking-[0.18em]">
              {user.email}
            </span>
            <button
              type="button"
              onClick={onLogout}
              data-testid="admin-logout"
              className="inline-flex items-center gap-2 px-4 py-2 border border-white/20 hover:border-gold hover:text-gold rounded-full text-xs uppercase tracking-[0.18em] transition-colors"
            >
              <LogOut size={14} /> Sign out
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-[1400px] mx-auto px-6 lg:px-10 py-12">
        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-6 mb-10">
          <div>
            <h1 className="font-display text-4xl lg:text-5xl">Customer Reviews</h1>
            <p className="mt-2 text-white/55 text-sm">
              Approve reviews to show them on the public site, or delete spam.
            </p>
          </div>
          <button
            type="button"
            onClick={load}
            disabled={loading}
            data-testid="admin-refresh"
            className="inline-flex items-center gap-2 px-4 py-2 border border-white/20 hover:border-gold hover:text-gold rounded-full text-xs uppercase tracking-[0.18em] self-start sm:self-end"
          >
            <RefreshCw size={14} className={loading ? "animate-spin" : ""} /> Refresh
          </button>
        </div>

        {/* Tabs */}
        <div className="flex flex-wrap gap-2 mb-8">
          {[
            { k: "all", label: "All" },
            { k: "pending", label: "Pending" },
            { k: "approved", label: "Published" },
          ].map((t) => (
            <button
              key={t.k}
              type="button"
              onClick={() => setFilter(t.k)}
              data-testid={`admin-filter-${t.k}`}
              className={`px-5 py-2 rounded-full text-xs uppercase tracking-[0.18em] border transition-colors ${
                filter === t.k
                  ? "bg-gold text-black border-gold"
                  : "border-white/15 text-white/70 hover:border-gold hover:text-gold"
              }`}
            >
              {t.label} <span className="ml-1 opacity-60">({counts[t.k]})</span>
            </button>
          ))}
        </div>

        {/* List */}
        {loading ? (
          <div className="text-white/50 py-20 text-center">Loading reviews...</div>
        ) : filtered.length === 0 ? (
          <div className="text-white/50 py-20 text-center border border-dashed border-white/10 rounded-sm">
            No reviews here yet.
          </div>
        ) : (
          <div className="space-y-4">
            {filtered.map((r) => (
              <ReviewRow
                key={r.id}
                review={r}
                onApprove={(v) => approve(r.id, v)}
                onDelete={() => remove(r.id)}
              />
            ))}
          </div>
        )}
      </main>
    </div>
  );
}

function ReviewRow({ review, onApprove, onDelete }) {
  return (
    <article
      data-testid={`admin-review-${review.id}`}
      className={`bg-[#0a0a0a] border ${
        review.approved ? "border-gold/30" : "border-white/10"
      } rounded-sm p-6 lg:p-8 transition-colors`}
    >
      <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-6">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-3 mb-3">
            <div className="flex items-center gap-0.5 text-gold">
              {Array.from({ length: 5 }).map((_, i) => (
                <Star
                  key={i}
                  size={14}
                  fill={i < review.rating ? "currentColor" : "none"}
                  strokeWidth={i < review.rating ? 0 : 1.5}
                  className={i < review.rating ? "" : "text-white/25"}
                />
              ))}
            </div>
            <span className="text-xs text-white/40">
              {new Date(review.created_at).toLocaleDateString(undefined, {
                year: "numeric",
                month: "short",
                day: "numeric",
              })}
            </span>
            {review.approved && (
              <span className="ml-auto lg:hidden inline-flex items-center gap-1 px-2 py-0.5 bg-gold/15 border border-gold/40 text-gold text-[10px] uppercase tracking-[0.18em] rounded-full">
                Published
              </span>
            )}
          </div>
          <blockquote className="font-display italic text-xl text-white/90 leading-snug">
            &ldquo;{review.quote}&rdquo;
          </blockquote>
          <div className="mt-4 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm">
            <span className="text-white font-medium">{review.name}</span>
            {review.location && (
              <span className="text-white/55">· {review.location}</span>
            )}
            {review.vehicle && (
              <span className="text-white/55">· {review.vehicle}</span>
            )}
          </div>
        </div>

        <div className="flex flex-col sm:flex-row lg:flex-col gap-3 lg:items-end shrink-0">
          {review.approved ? (
            <button
              type="button"
              onClick={() => onApprove(false)}
              data-testid={`admin-unpublish-${review.id}`}
              className="inline-flex items-center justify-center gap-2 px-4 py-2 border border-gold text-gold hover:bg-gold hover:text-black rounded-full text-xs uppercase tracking-[0.18em] transition-colors"
            >
              <Check size={14} /> Published — Click to hide
            </button>
          ) : (
            <button
              type="button"
              onClick={() => onApprove(true)}
              data-testid={`admin-approve-${review.id}`}
              className="inline-flex items-center justify-center gap-2 px-5 py-2 bg-gold text-black hover:bg-[#F3E5AB] rounded-full text-xs uppercase tracking-[0.18em] font-medium transition-colors"
            >
              <Check size={14} /> Approve &amp; Publish
            </button>
          )}
          <button
            type="button"
            onClick={onDelete}
            data-testid={`admin-delete-${review.id}`}
            className="inline-flex items-center justify-center gap-2 px-4 py-2 border border-white/15 text-white/70 hover:border-red-500/60 hover:text-red-400 rounded-full text-xs uppercase tracking-[0.18em] transition-colors"
          >
            <Trash2 size={14} /> Delete
          </button>
        </div>
      </div>
    </article>
  );
}
